import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * SEOHelmet 컴포넌트
 * 페이지별로 동적인 메타 태그를 관리하여 SEO를 최적화합니다.
 * 
 * @param {string} title - 페이지 제목
 * @param {string} description - 페이지 설명 (검색 결과에 표시)
 * @param {string} keywords - 키워드 (쉼표로 구분)
 * @param {string} image - OG 이미지 URL (소셜 미디어 공유 시 표시)
 * @param {string} url - 현재 페이지의 전체 URL
 * @param {string} type - Open Graph 타입 (기본값: 'website', 블로그는 'article')
 * @param {string} author - 작성자 이름 (블로그 글인 경우)
 * @param {string} publishedTime - 게시 시간 (블로그 글인 경우)
 * @param {array} tags - 태그 배열 (블로그 글인 경우)
 */
const SEOHelmet = ({ 
  title, 
  description, 
  keywords, 
  image, 
  url,
  type = 'website',
  author,
  publishedTime,
  tags = []
}) => {
  // 기본 사이트 정보
  const siteTitle = '라떼이즈(Latte is) - AI 이력서 컨설팅';
  const siteName = '라떼이즈';
  const defaultDescription = '라떼이즈(Latteis) - AI와 함께 만드는 나만의 커리어 스토리. 라떼이즈닷컴에서 AI 이력서 컨설팅을 받아보세요.';
  const defaultKeywords = '라떼이즈, latteis, 라떼이즈닷컴, AI 이력서, 이력서 컨설팅, 커리어 컨설팅, AI 컨설팅';
  const defaultImage = 'https://latteis.com/og-image.png'; // 기본 OG 이미지 (나중에 실제 이미지로 변경 필요)
  
  // 제목 포맷팅
  const fullTitle = title ? `${title} | ${siteName}` : siteTitle;
  const pageDescription = description || defaultDescription;
  const pageKeywords = keywords ? `${keywords}, ${defaultKeywords}` : defaultKeywords;
  const pageImage = image || defaultImage;
  
  // URL이 상대 경로인 경우 절대 경로로 변환
  const absoluteUrl = url && !url.startsWith('http') 
    ? `https://latteis.com${url}` 
    : url || 'https://latteis.com';
  
  return (
    <Helmet>
      {/* 기본 메타 태그 */}
      <title>{fullTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={pageKeywords} />
      {author && <meta name="author" content={author} />}
      
      {/* Open Graph 메타 태그 (페이스북, 카카오톡 등) */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={absoluteUrl} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="ko_KR" />
      
      {/* 블로그 글인 경우 추가 메타 태그 */}
      {type === 'article' && (
        <>
          {publishedTime && <meta property="article:published_time" content={publishedTime} />}
          {author && <meta property="article:author" content={author} />}
          {tags.length > 0 && tags.map((tag, index) => (
            <meta key={index} property="article:tag" content={tag} />
          ))}
        </>
      )}
      
      {/* Twitter Card 메타 태그 */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={pageImage} />
      
      {/* 추가 SEO 메타 태그 */}
      <link rel="canonical" href={absoluteUrl} />
      <meta name="robots" content="index, follow" />
      
      {/* 모바일 최적화 */}
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      
      {/* 언어 설정 */}
      <meta httpEquiv="content-language" content="ko" />
    </Helmet>
  );
};

export default SEOHelmet;
